'use client';
import { useState, useCallback } from 'react';
import { useToast } from '../contexts/toast-context';
import { createApiClient, buildFrontendUrl } from '@textile-erp/core';
export function useApi() {
    const [state, setState] = useState({
        loading: false,
        error: null,
        data: null,
    });
    const { showSuccess, showError } = useToast();
    const execute = useCallback(async (apiCall, options = {}) => {
        const { showSuccessToast = true, showErrorToast = true, successMessage = 'Operation completed successfully', errorMessage = 'Operation failed', } = options;
        setState(prev => ({ ...prev, loading: true, error: null }));
        try {
            const result = await apiCall();
            setState({
                loading: false,
                error: null,
                data: result,
            });
            if (showSuccessToast) {
                showSuccess('Success', successMessage);
            }
            return result;
        }
        catch (error) {
            const errorMsg = error?.message || error?.toString() || errorMessage;
            setState({
                loading: false,
                error: errorMsg,
                data: null,
            });
            if (showErrorToast) {
                showError('Error', errorMsg);
            }
            return null;
        }
    }, [showSuccess, showError]);
    const reset = useCallback(() => {
        setState({ loading: false, error: null, data: null });
    }, []);
    return {
        ...state,
        execute,
        reset,
    };
}
// Specialized hooks for common patterns
export function useFormApi() {
    const api = useApi();
    const submitForm = useCallback(async (formData, apiCall, options = {}) => {
        const { onSuccess, onError, ...apiOptions } = options;
        const result = await api.execute(() => apiCall(formData), {
            successMessage: 'Form submitted successfully',
            errorMessage: 'Failed to submit form',
            ...apiOptions,
        });
        if (result && onSuccess) {
            onSuccess(result);
        }
        else if (api.error && onError) {
            onError(api.error);
        }
        return result;
    }, [api]);
    return {
        ...api,
        submitForm,
    };
}
export function useDeleteApi() {
    const api = useApi();
    const deleteItem = useCallback(async (deleteCall, options = {}) => {
        const { itemName = 'item', onSuccess, ...apiOptions } = options;
        const result = await api.execute(deleteCall, {
            successMessage: `${itemName} deleted successfully`,
            errorMessage: `Failed to delete ${itemName}`,
            ...apiOptions,
        });
        if (result && onSuccess) {
            onSuccess();
        }
        return result;
    }, [api]);
    return {
        ...api,
        deleteItem,
    };
}
// Hook for handling multiple API states
export function useMultipleApi() {
    const [states, setStates] = useState({});
    const { showSuccess, showError } = useToast();
    const execute = useCallback(async (key, apiCall, options = {}) => {
        const { showSuccessToast = true, showErrorToast = true, successMessage = 'Operation completed successfully', errorMessage = 'Operation failed', } = options;
        setStates(prev => ({
            ...prev,
            [key]: { ...prev[key], loading: true, error: null },
        }));
        try {
            const result = await apiCall();
            setStates(prev => ({
                ...prev,
                [key]: { loading: false, error: null, data: result },
            }));
            if (showSuccessToast) {
                showSuccess('Success', successMessage);
            }
            return result;
        }
        catch (error) {
            const errorMsg = error?.message || error?.toString() || errorMessage;
            setStates(prev => ({
                ...prev,
                [key]: { loading: false, error: errorMsg, data: null },
            }));
            if (showErrorToast) {
                showError('Error', errorMsg);
            }
            return null;
        }
    }, [showSuccess, showError]);
    const getState = useCallback((key) => {
        return states[key] || { loading: false, error: null, data: null };
    }, [states]);
    const reset = useCallback((key) => {
        if (key) {
            setStates(prev => ({
                ...prev,
                [key]: { loading: false, error: null, data: null },
            }));
        }
        else {
            setStates({});
        }
    }, []);
    const isAnyLoading = Object.values(states).some(state => state.loading);
    return {
        execute,
        getState,
        reset,
        isAnyLoading,
        states,
    };
}
// ✅ Updated to use core API client
// Create a shared API client for UI components using frontend proxy
const uiApiClient = createApiClient(buildFrontendUrl(''));
// Utility function for common fetch patterns - now uses core API client
export function createApiCall(url, options = {}) {
    return async () => {
        const method = (options.method || 'GET');
        // Extract body data if present
        const bodyData = options.body ? JSON.parse(options.body) : undefined;
        // Use core API client methods
        switch (method) {
            case 'GET':
                return uiApiClient.get(url);
            case 'POST':
                return uiApiClient.post(url, bodyData);
            case 'PUT':
                return uiApiClient.put(url, bodyData);
            case 'DELETE':
                return uiApiClient.delete(url);
            default:
                return uiApiClient.get(url);
        }
    };
}
// Helper for common CRUD operations
export function createCrudApi(baseUrl) {
    return {
        getAll: () => createApiCall(baseUrl),
        getById: (id) => createApiCall(`${baseUrl}/${id}`),
        create: (data) => createApiCall(baseUrl, {
            method: 'POST',
            body: JSON.stringify(data),
        }),
        update: (id, data) => createApiCall(`${baseUrl}/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        }),
        delete: (id) => createApiCall(`${baseUrl}/${id}`, {
            method: 'DELETE',
        }),
    };
}
